class ProductContentGrid extends HTMLElement {
  #mediaQuery = window.matchMedia('(max-width: 749px)');
  #reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #timer;
  #cycleResetTimer;
  #clone;
  #index = 0;

  connectedCallback() {
    this.#mediaQuery.addEventListener('change', this.#update);
    this.#reducedMotionQuery.addEventListener('change', this.#update);
    this.addEventListener('transitionend', this.#handleTransitionEnd);
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    this.#update();
  }

  disconnectedCallback() {
    this.#mediaQuery.removeEventListener('change', this.#update);
    this.#reducedMotionQuery.removeEventListener('change', this.#update);
    this.removeEventListener('transitionend', this.#handleTransitionEnd);
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    this.#stop();
  }

  get #track() {
    return this.querySelector('.product-content-grid__track');
  }

  #update = () => {
    if (this.#mediaQuery.matches && !this.#reducedMotionQuery.matches) {
      this.#start();
    } else {
      this.#stop();
    }
  };

  #start() {
    const track = this.#track;
    if (!track || this.#clone || track.children.length < 2) return;

    this.#clone = track.firstElementChild?.cloneNode(true);
    if (!(this.#clone instanceof HTMLElement)) return;

    this.#clone.setAttribute('aria-hidden', 'true');
    this.#clone.removeAttribute('data-shopify-editor-block');
    track.append(this.#clone);
    this.#index = 0;
    track.style.transform = 'translate3d(0, 0, 0)';
    this.#timer = window.setInterval(this.#advance, 3000);
  }

  #stop() {
    window.clearInterval(this.#timer);
    this.#timer = undefined;
    window.clearTimeout(this.#cycleResetTimer);
    this.#cycleResetTimer = undefined;

    const track = this.#track;
    if (this.#clone?.isConnected) this.#clone.remove();
    this.#clone = undefined;
    this.#index = 0;
    if (track) {
      track.style.transition = 'none';
      track.style.transform = 'translate3d(0, 0, 0)';
      requestAnimationFrame(() => {
        if (track.isConnected) track.style.removeProperty('transition');
      });
    }
  }

  #advance = () => {
    const track = this.#track;
    if (!track || !this.#clone) return;

    const firstItem = track.firstElementChild;
    if (!(firstItem instanceof HTMLElement) || firstItem.clientWidth === 0) return;

    const trackStyles = window.getComputedStyle(track);
    const gap = Number.parseFloat(trackStyles.columnGap || trackStyles.gap) || 0;
    const step = firstItem.getBoundingClientRect().width + gap;

    this.#index += 1;
    track.style.transform = `translate3d(-${this.#index * step}px, 0, 0)`;

    // The first slide is appended as a duplicate. After sliding to that copy,
    // reset invisibly to the original first slide. The timer is a fallback for
    // browsers that do not emit transitionend after a Theme Editor update.
    const originalCount = track.children.length - 1;
    if (this.#index === originalCount) {
      window.clearTimeout(this.#cycleResetTimer);
      this.#cycleResetTimer = window.setTimeout(this.#returnToFirst, 420);
    }
  };

  #handleTransitionEnd = (event) => {
    if (event.target !== this.#track || this.#index === 0 || !this.#clone) return;
    const originalCount = this.#track.children.length - 1;
    if (this.#index !== originalCount) return;

    this.#returnToFirst();
  };

  #returnToFirst = () => {
    const track = this.#track;
    if (!track || !this.#clone) return;

    window.clearTimeout(this.#cycleResetTimer);
    this.#cycleResetTimer = undefined;
    this.#index = 0;
    track.style.transition = 'none';
    track.style.transform = 'translate3d(0, 0, 0)';
    requestAnimationFrame(() => {
      if (track.isConnected) track.style.removeProperty('transition');
    });
  };

  #handleSectionLoad = (event) => {
    if (!(event.target instanceof Element) || !event.target.contains(this)) return;
    this.#stop();
    this.#update();
  };
}

if (!customElements.get('product-content-grid')) {
  customElements.define('product-content-grid', ProductContentGrid);
}
